import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeSlash, CheckCircle, Spinner, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const MARKETING_URL = 'https://herchainsolutions.com';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    const validate = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/reset-password/validate`, { params: { token } });
        if (!mounted) return;
        setValid(Boolean(res.data?.valid));
        setReason(res.data?.reason || '');
        setEmail(res.data?.email || '');
      } catch (e) {
        if (!mounted) return;
        setValid(false);
        setReason('invalid');
      } finally {
        if (mounted) setValidating(false);
      }
    };
    if (token) validate(); else { setValidating(false); setValid(false); setReason('missing'); }
    return () => { mounted = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not reset password. Please request a new link.';
      setError(typeof msg === 'string' ? msg : 'Could not reset password.');
    } finally {
      setLoading(false);
    }
  };

  const Header = () => (
    <header className="text-center">
      <h1 className="font-bold tracking-tight" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
        <span className="text-[#3D1D72] text-4xl sm:text-5xl">HERChain</span>{' '}
        <span className="text-[#D4C5F0] text-4xl sm:text-5xl">Solutions</span>
      </h1>
      <p className="mt-3 text-sm sm:text-base text-slate-600" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
        Procurement Management Built for Caribbean Hospitality
      </p>
    </header>
  );

  const Footer = () => (
    <footer className="mt-10 text-center text-xs text-slate-500" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
      <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-3">
        <Link to="/privacy-policy" className="hover:text-[#3D1D72]">Privacy Policy</Link>
        <span className="text-slate-300">·</span>
        <Link to="/terms-of-service" className="hover:text-[#3D1D72]">Terms of Service</Link>
        <span className="text-slate-300">·</span>
        <a href={MARKETING_URL} className="hover:text-[#3D1D72]">herchainsolutions.com</a>
      </nav>
      <p>© {new Date().getFullYear()} HERChain Solutions. All rights reserved.</p>
    </footer>
  );

  return (
    <div
      className="min-h-screen bg-white flex items-center justify-center px-4 py-10"
      style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <Header />

        <div
          className="mt-8 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8"
          style={{ boxShadow: '0 1px 3px rgba(61, 29, 114, 0.05), 0 8px 24px rgba(61, 29, 114, 0.06)' }}
        >
          {validating && (
            <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="reset-validating">
              <Spinner size={18} className="animate-spin" /> Validating reset link…
            </div>
          )}

          {!validating && !valid && (
            <div className="space-y-4" data-testid="reset-invalid">
              <div className="flex items-center gap-2 text-red-700">
                <Warning size={20} weight="fill" />
                <h2 className="text-xl font-bold">Reset link is not valid</h2>
              </div>
              <p className="text-sm text-slate-700">
                {reason === 'expired' && 'This link has expired. Please request a new password reset.'}
                {reason === 'used' && 'This link has already been used. Please request a new one.'}
                {(reason === 'not_found' || reason === 'invalid' || reason === 'missing') &&
                  'This password reset link is invalid. Please request a new one.'}
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full h-11 rounded-lg bg-[#3D1D72] hover:bg-[#2D1652] text-white font-semibold transition-colors"
                data-testid="reset-back-to-login-btn"
              >
                Back to sign in
              </button>
            </div>
          )}

          {!validating && valid && !done && (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form" noValidate>
              <h2 className="text-xl font-bold text-[#3D1D72]">Set a new password</h2>
              {email && <p className="text-sm text-slate-600">Resetting password for <span className="font-medium">{email}</span></p>}

              <div className="space-y-1.5">
                <label htmlFor="reset-pw" className="block text-sm font-medium text-slate-700">New password</label>
                <div className="relative">
                  <input
                    id="reset-pw"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="w-full h-11 px-3 pr-11 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#3D1D72]/30 focus:border-[#3D1D72] text-sm"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-[#3D1D72]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="reset-confirm" className="block text-sm font-medium text-slate-700">Confirm password</label>
                <input
                  id="reset-confirm"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Retype your new password"
                  className="w-full h-11 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#3D1D72]/30 focus:border-[#3D1D72] text-sm"
                  data-testid="reset-confirm-input"
                />
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" role="alert" data-testid="reset-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-lg bg-[#3D1D72] hover:bg-[#2D1652] text-white font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="reset-submit-btn"
              >
                {loading ? <Spinner size={18} className="animate-spin" /> : null}
                {loading ? 'Updating…' : 'Reset password'}
              </button>
            </form>
          )}

          {done && (
            <div className="space-y-4 text-center" data-testid="reset-done">
              <div className="flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle size={32} weight="fill" className="text-green-600" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-[#3D1D72]">Password updated</h2>
              <p className="text-sm text-slate-700">Your password has been reset. You can now sign in with your new password.</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full h-11 rounded-lg bg-[#3D1D72] hover:bg-[#2D1652] text-white font-semibold transition-colors"
                data-testid="reset-go-signin-btn"
              >
                Go to sign in
              </button>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default ResetPasswordPage;
